// Instance-type → cores/memory display + credit-multiplier helpers.
//
// Users don't care about AWS instance type names ("m8gd.8xlarge") —
// they care about how much compute the stack got. This module converts
// the raw type string into a friendly "32c · 128 GB" chip.
//
// Source of truth is `instanceMetadata` in lambda-start-job/main.go.
// Kept as a small in-code map because the whitelist changes rarely and
// pulling /catalog on every list-row render would be silly. If the
// backend whitelist adds a type not listed here, we fall back to
// showing the raw type string — safe degradation.

interface InstanceProfile {
  vcpu: number;
  ramGB: number;
}

// Mirror of `instanceMetadata` in lambda-start-job/main.go — every
// type in either the ALLOWED_INSTANCE_TYPES whitelist or the
// launchTier* fallback slices must be listed here or formatInstance
// falls back to the generic "spot compute" label (which is still safe
// UX — just less informative than "64 vCPU · 512 GB RAM"). Grep the
// backend map on every whitelist change and mirror.
const INSTANCE_PROFILE: Record<string, InstanceProfile> = {
  // Graviton4 c-family (compute-opt, local NVMe)
  'c8gd.2xlarge':  { vcpu: 8,   ramGB: 16 },
  'c8gd.4xlarge':  { vcpu: 16,  ramGB: 32 },
  'c8gd.8xlarge':  { vcpu: 32,  ramGB: 64 },
  'c8gd.16xlarge': { vcpu: 64,  ramGB: 128 },
  // Graviton4 m-family (balanced, local NVMe) — LAUNCH DEFAULT is 8xl
  'm8gd.2xlarge':  { vcpu: 8,   ramGB: 32 },
  'm8gd.4xlarge':  { vcpu: 16,  ramGB: 64 },
  'm8gd.8xlarge':  { vcpu: 32,  ramGB: 128 },
  'm8gd.16xlarge': { vcpu: 64,  ramGB: 256 },
  // Graviton4 r-family (RAM-opt, local NVMe) — big-drizzle tier
  'r8gd.4xlarge':  { vcpu: 16,  ramGB: 128 },
  'r8gd.16xlarge': { vcpu: 64,  ramGB: 512 },
  'r8gd.24xlarge': { vcpu: 96,  ramGB: 768 },
  'r8gd.48xlarge': { vcpu: 192, ramGB: 1536 },
  // Graviton3 fallbacks — same shape at a separate spot pool
  'c7gd.2xlarge':  { vcpu: 8,   ramGB: 16 },
  'c7gd.16xlarge': { vcpu: 64,  ramGB: 128 },
  'm7gd.2xlarge':  { vcpu: 8,   ramGB: 32 },
  'm7gd.8xlarge':  { vcpu: 32,  ramGB: 128 },
  'm7gd.16xlarge': { vcpu: 64,  ramGB: 256 },
  'r7gd.16xlarge': { vcpu: 64,  ramGB: 512 },
  // Storage-opt Graviton — 15 TB local NVMe swaps drizzle scratch
  // out of RAM pressure. Deep i-series spot pool (different customer
  // base than r-series' AI-training drain).
  'i4g.16xlarge':  { vcpu: 64,  ramGB: 512 },
  'i8g.16xlarge':  { vcpu: 64,  ramGB: 512 },
  // Skoda tier — Graviton2 c-family, 0.5× credit multiplier.
  'c6gd.2xlarge':  { vcpu: 8,   ramGB: 16 },
  'c6gd.4xlarge':  { vcpu: 16,  ramGB: 32 },
  // Ludicrous escape hatch (retained from earlier fleet plan).
  'c8g.48xlarge':  { vcpu: 192, ramGB: 384 },
};


// Fleet auto-select rule from lambda-start-job/main.go::selectTierForWorkload.
// Three-tier picker keyed on frames vs 500 + drizzle on/off; matches the
// 0.5× / 1× / 2× billing multiplier 1:1. If frontend logic drifts from
// backend, the review-page cost preview under-quotes and the ledger
// debit will surprise the user.
const TIER_ESCALATION_FRAMES = 500;
const LAUNCH_TIER_DEFAULT = 'm8gd.8xlarge';
const LAUNCH_TIER_SMALL = 'c6gd.2xlarge';
const LAUNCH_TIER_BIG = 'r8gd.16xlarge';

// Pricing constants — mirror lambda-start-job/ledger.go. Update both.
//
// Shape: step function on FREE_FRAMES, not marginal.
//   ≤ FREE_FRAMES: 0 credits (drizzle or not — bread and butter)
//   > FREE_FRAMES, non-drizzle: frames × 1 credit
//   > FREE_FRAMES, drizzle:     frames × DRIZZLE_CREDITS_PER_FRAME
//
// Small stacks are free because a modern laptop does them in minutes;
// anything larger has a story to pay for. A 300-frame drizzle test on
// r8gd.16xlarge runs in ~4 min and costs us ~11¢ AWS spot; charging for
// it earns rounding-error revenue and discourages trial. Instance tier
// is NOT a pricing input — computeCredits() is the single source of
// truth on the frontend.
export const FREE_FRAMES = 500;
export const DRIZZLE_CREDITS_PER_FRAME = 2;

/** Credit cost for a job, mirroring backend jobDebitCredits. Pure
 *  function of (frames, drizzle). */
export const computeCredits = (frames: number, drizzle: number): number => {
  if (frames <= FREE_FRAMES) return 0;
  return drizzle > 1 ? frames * DRIZZLE_CREDITS_PER_FRAME : frames;
};

/** Human display for an instance type — "32 vCPU · 128 GB RAM" when
 *  we have profile info, "Spot compute" when we don't. Long-form on
 *  purpose: reads better on the public share page and on marketing
 *  surfaces where the audience isn't skimming AWS docs.
 *
 *  Never surfaces the raw AWS type ("i8g.16xlarge") — users don't need
 *  to know the vendor SKU, and a missing map entry shouldn't turn a
 *  polished spec chip into a leaked internal string. If the whitelist
 *  gains a new type the map hasn't been updated for, users still see
 *  a valid label; developers grep the file for "TODO whitelist" —
 *  actually, no TODO, just fix the map. */
export const formatInstance = (instanceType: string | undefined): string => {
  if (!instanceType) return '—';
  const p = INSTANCE_PROFILE[instanceType];
  if (!p) return 'Spot compute';
  return `${p.vcpu} vCPU · ${p.ramGB} GB RAM`;
};


/** Predict which instance the Fleet auto-picker will land on for a
 *  given workload. Mirrors selectTierForWorkload in the backend so the
 *  NewJob review page can preview cost before submitting. */
export const predictInstance = (frameCount: number, drizzle: number): string => {
  if (frameCount < TIER_ESCALATION_FRAMES) {
    return drizzle > 1 ? LAUNCH_TIER_DEFAULT : LAUNCH_TIER_SMALL;
  }
  return drizzle > 1 ? LAUNCH_TIER_BIG : LAUNCH_TIER_DEFAULT;
};
